import {
  listEventAppModerators,
  listRole,
  createEventAppModerator,
  User,
  deleteEventAppModerator,
  updateEventAppModeratorRoles,
  UserRoleBroadcastMappingRequestRequest,
  listEmail
} from '@goldcast/api/backend';

import { uploadUserImage } from '@/studio/speakers/store/actions';

const mapStaffRolesWithBroadcasts = staffData => {
  const staffMembers = staffData.map(staff => {
    const rolesWithBroadcasts = staff.roles.reduce((acc, role) => {
      acc[role.role.id] = role.broadcasts;
      return acc;
    }, {});

    return {
      ...staff,
      staffRoles: rolesWithBroadcasts
    };
  });

  return staffMembers;
};

export default {
  async loadStaffMembers(
    { commit, dispatch, rootGetters },
    { eventId, hardRefresh = false }: { eventId: string; hardRefresh?: boolean }
  ) {
    if (!rootGetters['org/isEventRolesEnabled']) {
      return;
    }

    if (hardRefresh) {
      commit('setIsLoading', true);
    }

    return new Promise((resolve, reject) => {
      listEventAppModerators({
        queryParams: {
          event: eventId,
          user_type: 'staff',
          offset: 0,
          ordering: 'created_at'
        }
      })
        .then(staffData => {
          const staffMembers = mapStaffRolesWithBroadcasts(staffData || []);
          commit('setStaff', staffMembers);
          commit('setTotalStaffCount', (staffData as any)?.length || 0);
          if (hardRefresh) {
            commit('setIsLoading', false);
          }

          resolve('success');
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Staff, Please Try Again', mode: 'error' },
            { root: true }
          );

          if (hardRefresh) {
            commit('setIsLoading', false);
          }

          reject(error);
        });
    });
  },
  async loadRoles({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      listRole({
        queryParams: {
          realm: 'event_app',
          limit: 10,
          role_group: 'Staff Roles'
        }
      })
        .then(roles => {
          const updatedRoles = roles.results
            ?.filter(role => ['Producer', 'Coordinator'].includes(role.name))
            .map(role => {
              return {
                ...role,
                label: role.name,
                value: role.id
              };
            });
          commit('setRoles', updatedRoles || []);
          resolve('success');
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Roles, Please Try Again', mode: 'error' },
            { root: true }
          );

          reject(error);
        });
    });
  },
  async loadSystemStaffRoles({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      listRole({
        queryParams: {
          realm: 'event_app',
          role_group: 'Staff Roles',
          is_custom: false,
          limit: 10
        }
      })
        .then(roles => {
          const updatedRoles = roles.results
            ?.filter(role => role.name === 'Producer')
            .map(role => {
              return {
                ...role,
                label: role.name,
                value: role.id
              };
            });
          commit('setSystemStaffRoles', updatedRoles);
          resolve(updatedRoles);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Staff Roles, Please Try Again', mode: 'error' },
            { root: true }
          );
          reject(error);
        });
    });
  },
  async addStaffMember(
    { rootState },
    {
      user,
      file,
      broadcastIds,
      role,
      send_calendar_invite,
      send_email_invite
    }: {
      user: User;
      file: File;
      broadcastIds: string[];
      role: number;
      send_calendar_invite?: boolean;
      send_email_invite?: boolean;
    }
  ) {
    const eventId: string = rootState.studioEventList.currentEventId;
    return new Promise((resolve, reject) => {
      uploadUserImage({ imageFile: file, userId: user.id }).then(profilePictureUrl => {
        if (profilePictureUrl) {
          //@ts-ignore
          user.profile_picture_url = profilePictureUrl;
        }

        return createEventAppModerator({
          body: {
            event: eventId,
            user,
            broadcasts: broadcastIds,
            event_app_role: role,
            user_type: 'staff',
            send_calendar_invite,
            send_email_invite
          }
        })
          .then(staff => {
            resolve(staff);
          })
          .catch(reject);
      });
    });
  },
  async partialUpdateStaffMember(
    { rootState },
    {
      staffId,
      roles,
      coreUserId
    }: { staffId: number; roles: UserRoleBroadcastMappingRequestRequest[]; coreUserId: string }
  ) {
    const eventId: string = rootState.studioEventList.currentEventId;

    return new Promise((resolve, reject) => {
      updateEventAppModeratorRoles({
        body: {
          event: eventId,
          user: coreUserId,
          roles
        },
        id: staffId
      })
        .then(staff => {
          resolve(staff);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async deleteStaffMember({ commit }, { staffId }: { staffId: number }) {
    return new Promise((resolve, reject) => {
      deleteEventAppModerator({
        id: staffId
      })
        .then(() => {
          commit('deleteStaff', staffId);
          resolve('success');
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async updateStaffMember(
    { dispatch, state, commit, rootGetters },
    { staffId, user, file }: { staffId: string; user: User; file: File }
  ) {
    const userId = state.staff.find(staff => staff.id === staffId)?.user.id;
    const eventId = rootGetters['studioEventList/getCurrentEvent'].id;

    return new Promise((resolve, reject) => {
      if (!file) {
        dispatch('studioSpeakers/updateUser', { userId, user, eventId }, { root: true })
          .then(updatedUser => {
            commit('updateStaff', {
              staffId,
              staff: { user: updatedUser }
            });
            resolve(updatedUser);
          })
          .catch(reject);
      } else {
        uploadUserImage({ imageFile: file, userId })
          .then(async imageURL => {
            const payload = {
              ...user,
              profile_picture_url: imageURL
            };
            const updatedUser = await dispatch(
              'studioSpeakers/updateUser',
              {
                eventId,
                userId,
                user: payload
              },
              { root: true }
            );
            commit('updateStaff', {
              staffId,
              staff: { user: updatedUser }
            });
            resolve(updatedUser);
          })
          .catch(error => {
            reject(error);
          });
      }
    });
  },
  async fetchStaffEmailConfig({ commit, dispatch, rootGetters }) {
    const currentEventId = rootGetters['studioEventList/getCurrentEvent']?.id;

    if (!currentEventId) {
      return;
    }

    commit('setIsStaffEmailConfigLoading', true);
    return new Promise((resolve, reject) => {
      listEmail({
        queryParams: {
          event: currentEventId,
          email_type: 'StaffInvite'
        }
      })
        .then(response => {
          commit('setStaffInviteEmailConfig', response[0]);
          resolve(response);
        })
        .then(() => {
          commit('setIsStaffEmailConfigLoading', false);
          resolve('success');
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Staff Email Config, Please Try Again', mode: 'error' },
            { root: true }
          );

          commit('setIsStaffEmailConfigLoading', false);
          reject(error);
        });
    });
  }
};
